import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ShieldCheck, CheckCircle, XCircle, Badge, Eye, CreditCard, Plus, Pencil, Trash2, ToggleLeft, ToggleRight, BarChart3, Camera, FileText, User, MapPin, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api, { API_BASE } from '@/lib/api';
import AdminPromoCodes from '../components/AdminPromoCodes';

const COLORS = ['#E6202E', '#4285F4', '#34A853', '#FBBC05'];

function MetricsChart({ data }) {
  if (!data || data.length === 0) return <p className="text-gray-400 text-center py-8">Sin datos</p>;
  const keys = ['users', 'providers', 'subscriptions', 'reviews', 'promos'];
  const labels = ['Usuarios', 'Cuidadores', 'Suscripciones', 'Reseñas'];
  const maxVal = Math.max(...data.flatMap(d => keys.map(k => d[k] || 0)), 1);

  return (
    <div data-testid="metrics-chart">
      <div className="flex gap-4 mb-4 flex-wrap">
        {keys.map((k, i) => (
          <div key={k} className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: COLORS[i] }} />
            <span className="text-gray-600">{labels[i]}</span>
          </div>
        ))}
      </div>
      <div className="flex items-end gap-3 h-48">
        {data.map((d, idx) => (
          <div key={idx} className="flex-1 flex flex-col items-center gap-1">
            <div className="flex items-end gap-0.5 h-40 w-full">
              {keys.map((k, ki) => {
                const h = maxVal > 0 ? ((d[k] || 0) / maxVal) * 100 : 0;
                return (
                  <div key={k} className="flex-1 flex flex-col items-center justify-end h-full">
                    <span className="text-[10px] text-gray-500 mb-1">{d[k] || 0}</span>
                    <div
                      className="w-full rounded-t transition-all"
                      style={{ height: `${Math.max(h, 4)}%`, backgroundColor: COLORS[ki] }}
                    />
                  </div>
                );
              })}
            </div>
            <span className="text-xs text-gray-500 font-medium">{d.month}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlanModal({ plan, onClose, onSave }) {
  const [name, setName] = useState(plan?.name || '');
  const [duration, setDuration] = useState(plan?.duration_months || 1);
  const [price, setPrice] = useState(plan?.price_clp || 0);
  const [features, setFeatures] = useState((plan?.features || []).join('\n'));
  const [popular, setPopular] = useState(plan?.popular || false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name || !price) { toast.error('Nombre y precio son requeridos'); return; }
    setSaving(true);
    try {
      const data = {
        name, duration_months: Number(duration), price_clp: Number(price),
        features: features.split('\n').filter(f => f.trim()), popular
      };
      if (plan?.plan_id) {
        await api.put(`/admin/plans/${plan.plan_id}`, data);
        toast.success('Plan actualizado');
      } else {
        await api.post('/admin/plans', data);
        toast.success('Plan creado');
      }
      onSave();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="plan-modal">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md space-y-4">
        <h3 className="text-xl font-bold">{plan?.plan_id ? 'Editar Plan' : 'Nuevo Plan'}</h3>
        <div>
          <label className="text-sm font-medium text-gray-700">Nombre</label>
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Plan 1 Mes" data-testid="plan-name-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium text-gray-700">Duracion (meses)</label>
            <Input type="number" value={duration} onChange={e => setDuration(e.target.value)} min={1} data-testid="plan-duration-input" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Precio (CLP)</label>
            <Input type="number" value={price} onChange={e => setPrice(e.target.value)} min={0} data-testid="plan-price-input" />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Caracteristicas (una por linea)</label>
          <textarea value={features} onChange={e => setFeatures(e.target.value)}
            className="w-full border rounded-lg p-2 text-sm min-h-[100px] focus:outline-none focus:ring-2 focus:ring-[#E6202E]"
            placeholder="Chat ilimitado&#10;Contacto directo" data-testid="plan-features-input" />
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={popular} onChange={e => setPopular(e.target.checked)} className="rounded" />
          <span className="text-sm">Marcar como popular</span>
        </label>
        <div className="flex gap-3 pt-2">
          <Button onClick={onClose} variant="outline" className="flex-1">Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#E6202E] hover:bg-[#D31522] text-white" data-testid="plan-save-button">
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function LocationEditorModal({ provider, onClose, onSaved }) {
  const [lat, setLat] = useState(provider.latitude ?? '');
  const [lng, setLng] = useState(provider.longitude ?? '');
  const [comuna, setComuna] = useState(provider.comuna || '');
  const [address, setAddress] = useState(provider.address || '');
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);

  const geocodeAddress = async () => {
    if (!address.trim() && !comuna.trim()) {
      toast.error('Ingresá dirección o comuna primero');
      return;
    }
    const query = encodeURIComponent(`${address}, ${comuna}, Chile`.trim());
    setGeocoding(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1`);
      const data = await res.json();
      if (data && data[0]) {
        setLat(parseFloat(data[0].lat).toFixed(6));
        setLng(parseFloat(data[0].lon).toFixed(6));
        toast.success('Coordenadas encontradas');
      } else {
        toast.error('No se encontró la dirección');
      }
    } catch (e) {
      toast.error('Error al geocodificar');
    } finally {
      setGeocoding(false);
    }
  };

  const handleSave = async () => {
    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);
    if (Number.isNaN(latNum) || Number.isNaN(lngNum)) {
      toast.error('Latitud y longitud deben ser números válidos');
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/admin/providers/${provider.provider_id}/location`, {
        latitude: latNum,
        longitude: lngNum,
        comuna: comuna.trim() || undefined,
        address: address.trim() || undefined,
      });
      toast.success('Ubicación actualizada');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="location-editor-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <div className="flex items-center gap-2 mb-1">
          <MapPin className="w-5 h-5 text-blue-600" />
          <h3 className="text-xl font-bold">Editar ubicación</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">{provider.business_name || provider.user_name}</p>

        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Comuna</label>
            <Input value={comuna} onChange={(e) => setComuna(e.target.value)} placeholder="Ej: Providencia" data-testid="loc-comuna" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Dirección (opcional)</label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Ej: Av. Providencia 1234" data-testid="loc-address" />
          </div>
          <Button
            type="button"
            variant="outline"
            className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
            onClick={geocodeAddress}
            disabled={geocoding}
            data-testid="geocode-btn"
          >
            {geocoding ? 'Buscando...' : '🔍 Buscar coordenadas desde dirección'}
          </Button>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Latitud</label>
              <Input type="number" step="any" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="-33.4489" data-testid="loc-lat" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Longitud</label>
              <Input type="number" step="any" value={lng} onChange={(e) => setLng(e.target.value)} placeholder="-70.6693" data-testid="loc-lng" />
            </div>
          </div>

          {lat && lng && (
            <a
              href={`https://www.google.com/maps?q=${lat},${lng}`}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-blue-600 hover:underline flex items-center gap-1"
            >
              📍 Ver en Google Maps
            </a>
          )}
        </div>

        <div className="flex gap-2 mt-6">
          <Button variant="outline" onClick={onClose} className="flex-1" disabled={saving}>Cancelar</Button>
          <Button onClick={handleSave} className="flex-1 bg-[#E6202E] hover:bg-[#D31522]" disabled={saving} data-testid="save-location-btn">
            {saving ? 'Guardando...' : 'Guardar ubicación'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pendingProviders, setPendingProviders] = useState([]);
  const [allProviders, setAllProviders] = useState([]);
  const [plans, setPlans] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [editPlan, setEditPlan] = useState(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [sosConfig, setSosConfig] = useState({ active: false, phone: '', schedule: '', vet_name: '', start_hour: 8, end_hour: 20 });
  const [savingSos, setSavingSos] = useState(false);
  const [viewingDocs, setViewingDocs] = useState(null);
  const [providerStatusFilter, setProviderStatusFilter] = useState('all'); // all | online | incomplete
  const [locationProvider, setLocationProvider] = useState(null); // Provider being edited for location
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const userRes = await api.get('/auth/me');
      if (userRes.data.role !== 'admin') { navigate('/dashboard'); return; }
      const [statsRes, pendingRes, allRes, plansRes, metricsRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/providers/pending'),
        api.get('/admin/providers/all'),
        api.get('/admin/plans'),
        api.get('/admin/metrics')
      ]);
      setStats(statsRes.data);
      setPendingProviders(pendingRes.data);
      setAllProviders(allRes.data);
      setPlans(plansRes.data);
      setMetrics(metricsRes.data);
      try {
        const sosRes = await api.get('/admin/sos');
        setSosConfig(sosRes.data);
      } catch {}
      try {
        const clientsRes = await api.get('/admin/clients');
        setClients(clientsRes.data);
      } catch {}
    } catch (e) {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const approveProvider = async (providerId) => {
    try { await api.post(`/admin/providers/${providerId}/approve`); toast.success('Proveedor aprobado'); loadData(); }
    catch (e) { toast.error('Error'); }
  };
  const rejectProvider = async (providerId) => {
    const reason = prompt('Razon del rechazo:');
    if (!reason) return;
    try { await api.post(`/admin/providers/${providerId}/reject`, { reason }); toast.success('Proveedor rechazado'); loadData(); }
    catch (e) { toast.error('Error'); }
  };
  const verifyProvider = async (providerId) => {
    try { await api.post(`/admin/providers/${providerId}/verify`); toast.success('Proveedor verificado'); loadData(); }
    catch (e) { toast.error('Error'); }
  };
  const unverifyProvider = async (providerId) => {
    try { await api.post(`/admin/providers/${providerId}/unverify`); toast.success('Verificación removida'); loadData(); }
    catch (e) { toast.error('Error'); }
  };
  const togglePlan = async (planId) => {
    try { await api.post(`/admin/plans/${planId}/toggle`); toast.success('Estado del plan actualizado'); loadData(); }
    catch (e) { toast.error('Error'); }
  };
  const deletePlan = async (planId) => {
    if (!window.confirm('Eliminar este plan?')) return;
    try { await api.delete(`/admin/plans/${planId}`); toast.success('Plan eliminado'); loadData(); }
    catch (e) { toast.error('Error'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-16 h-16 border-4 border-[#E6202E] border-t-transparent rounded-full"></div></div>;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-panel">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-4xl font-bold mb-8">Panel de Administracion</h1>

        {stats && (
          <div className="grid md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-xl p-5 shadow-sm"><p className="text-gray-500 text-sm">Usuarios</p><p className="text-3xl font-bold text-[#E6202E]" data-testid="stat-users">{stats.total_users}</p></div>
            <div className="bg-white rounded-xl p-5 shadow-sm"><p className="text-gray-500 text-sm">Cuidadores</p><p className="text-3xl font-bold text-[#E6202E]" data-testid="stat-providers">{stats.total_providers}</p></div>
            <div className="bg-white rounded-xl p-5 shadow-sm"><p className="text-gray-500 text-sm">Pendientes</p><p className="text-3xl font-bold text-orange-500" data-testid="stat-pending">{stats.pending_providers}</p></div>
            <div className="bg-white rounded-xl p-5 shadow-sm"><p className="text-gray-500 text-sm">Verificados</p><p className="text-3xl font-bold text-green-600" data-testid="stat-verified">{stats.verified_providers}</p></div>
            <div className="bg-white rounded-xl p-5 shadow-sm"><p className="text-gray-500 text-sm">Suscripciones</p><p className="text-3xl font-bold text-[#E6202E]" data-testid="stat-subscriptions">{stats.active_subscriptions}</p></div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm">
          <div className="flex border-b overflow-x-auto">
            <button onClick={() => setActiveTab('pending')} className={`px-6 py-4 font-medium whitespace-nowrap ${activeTab === 'pending' ? 'text-[#E6202E] border-b-2 border-[#E6202E]' : 'text-gray-500'}`} data-testid="tab-pending">
              Pendientes ({pendingProviders.length})
            </button>
            <button onClick={() => setActiveTab('all')} className={`px-6 py-4 font-medium whitespace-nowrap ${activeTab === 'all' ? 'text-[#E6202E] border-b-2 border-[#E6202E]' : 'text-gray-500'}`} data-testid="tab-all">
              Cuidadores ({allProviders.filter(p => p.approved).length})
            </button>
            <button onClick={() => setActiveTab('clients')} className={`px-6 py-4 font-medium whitespace-nowrap ${activeTab === 'clients' ? 'text-[#E6202E] border-b-2 border-[#E6202E]' : 'text-gray-500'}`} data-testid="tab-clients">
              <User className="w-4 h-4 inline mr-1" />Clientes ({clients.length})
            </button>
            <button onClick={() => setActiveTab('plans')} className={`px-6 py-4 font-medium whitespace-nowrap ${activeTab === 'plans' ? 'text-[#E6202E] border-b-2 border-[#E6202E]' : 'text-gray-500'}`} data-testid="tab-plans">
              <CreditCard className="w-4 h-4 inline mr-1" />Planes ({plans.length})
            </button>
            <button onClick={() => setActiveTab('metrics')} className={`px-6 py-4 font-medium whitespace-nowrap ${activeTab === 'metrics' ? 'text-[#E6202E] border-b-2 border-[#E6202E]' : 'text-gray-500'}`} data-testid="tab-metrics">
              <BarChart3 className="w-4 h-4 inline mr-1" />Metricas
            </button>
            <button onClick={() => setActiveTab('sos')} className={`px-6 py-4 font-medium whitespace-nowrap ${activeTab === 'sos' ? 'text-[#E6202E] border-b-2 border-[#E6202E]' : 'text-gray-500'}`} data-testid="tab-sos">
              SOS Veterinario
            </button>
            <button
              onClick={() => setActiveTab('promos')}
              className={`px-6 py-4 font-medium whitespace-nowrap ${activeTab === 'promos' ? 'text-[#E6202E] border-b-2 border-[#E6202E]' : 'text-gray-500'}`}
              data-testid="tab-promos"
            >
              <Tag className="w-4 h-4 inline mr-1" />Códigos
            </button>
          </div>

          <div className="p-6">
            {activeTab === 'pending' && (
              pendingProviders.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No hay proveedores pendientes</p>
              ) : (
                <div className="space-y-4">
                  {pendingProviders.map(p => (
                    <div key={p.provider_id} className="p-4 bg-orange-50 border border-orange-200 rounded-xl">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-bold text-lg">{p.business_name}</h3>
                          <p className="text-sm text-gray-600">{p.comuna} - {p.phone}</p>
                          {p.description && <p className="text-sm text-gray-500 mt-1 line-clamp-2">{p.description}</p>}
                          
                          {/* Verification Status */}
                          {p.verification_status === 'pending' && (
                            <div className="mt-2 flex items-center gap-2">
                              <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                                <ShieldCheck className="w-3 h-3" />
                                Documentos pendientes de revision
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {/* View Documents Button */}
                          {(p.id_front_photo || p.id_back_photo || p.selfie_photo) && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => setViewingDocs(p)}
                              className="text-blue-600 border-blue-300"
                            >
                              <Camera className="w-4 h-4 mr-1" />Documentos
                            </Button>
                          )}
                          <Link to={`/provider/${p.provider_id}`}><Button variant="outline" size="sm"><Eye className="w-4 h-4" /></Button></Link>
                          <Button size="sm" onClick={() => approveProvider(p.provider_id)} className="bg-green-600 hover:bg-green-700"><CheckCircle className="w-4 h-4 mr-1" />Aprobar</Button>
                          <Button size="sm" variant="outline" onClick={() => rejectProvider(p.provider_id)} className="text-red-600 border-red-600"><XCircle className="w-4 h-4 mr-1" />Rechazar</Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}

            {activeTab === 'all' && (
              <div className="space-y-4">
                {/* Filters for online/incomplete */}
                {(() => {
                  const approved = allProviders.filter(p => p.approved);
                  const onlineCount = approved.filter(p => p.is_online_in_search).length;
                  const incompleteCount = approved.filter(p => !p.is_online_in_search).length;
                  return (
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      <button
                        onClick={() => setProviderStatusFilter('all')}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${providerStatusFilter === 'all' ? 'bg-[#E6202E] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                        data-testid="filter-all"
                      >
                        Todos ({approved.length})
                      </button>
                      <button
                        onClick={() => setProviderStatusFilter('online')}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${providerStatusFilter === 'online' ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}
                        data-testid="filter-online"
                      >
                        🟢 En buscador ({onlineCount})
                      </button>
                      <button
                        onClick={() => setProviderStatusFilter('incomplete')}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${providerStatusFilter === 'incomplete' ? 'bg-orange-500 text-white' : 'bg-orange-50 text-orange-700 hover:bg-orange-100'}`}
                        data-testid="filter-incomplete"
                      >
                        🟠 Incompletos ({incompleteCount})
                      </button>
                    </div>
                  );
                })()}

                {allProviders
                  .filter(p => p.approved)
                  .filter(p => {
                    if (providerStatusFilter === 'online') return p.is_online_in_search;
                    if (providerStatusFilter === 'incomplete') return !p.is_online_in_search;
                    return true;
                  })
                  .map(p => (
                  <div key={p.provider_id} className={`p-4 rounded-xl border ${p.is_online_in_search ? 'bg-green-50/50 border-green-200' : 'bg-orange-50/50 border-orange-200'}`}>
                    <div className="flex justify-between items-start gap-3 flex-wrap">
                      <div className="flex items-center gap-3 flex-1 min-w-[240px]">
                        <div className="w-12 h-12 rounded-lg bg-gray-200 overflow-hidden shrink-0">
                          {p.profile_photo ? (
                            <img src={p.profile_photo} alt="" className="w-full h-full object-cover" />
                          ) : p.photos?.[0] ? (
                            <img src={p.photos[0]} alt="" className="w-full h-full object-cover" />
                          ) : null}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-bold" data-testid={`provider-name-${p.provider_id}`}>{p.business_name || p.user_name || 'Sin nombre'}</h3>
                            {p.verified && <ShieldCheck className="w-5 h-5 text-[#E6202E]" />}
                            {p.is_online_in_search ? (
                              <span className="text-xs font-semibold text-green-700 bg-green-100 border border-green-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-green-500" />
                                En buscador
                              </span>
                            ) : (
                              <span className="text-xs font-semibold text-orange-700 bg-orange-100 border border-orange-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-orange-500" />
                                Incompleto
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{p.comuna || 'Sin comuna'}</p>
                          {!p.is_online_in_search && p.missing_fields?.length > 0 && (
                            <p className="text-xs text-orange-700 mt-1">
                              Falta: <span className="font-medium">{p.missing_fields.join(', ')}</span>
                            </p>
                          )}
                          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-600">
                            {p.email && (
                              <a href={`mailto:${p.email}?subject=U-CAN%20-%20Completa%20tu%20perfil%20de%20cuidador`} className="flex items-center gap-1 hover:text-[#E6202E]" data-testid={`mail-${p.provider_id}`}>
                                ✉️ {p.email}
                              </a>
                            )}
                            {p.phone && (
                              <a href={`https://wa.me/${String(p.phone).replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-green-600" data-testid={`wa-${p.provider_id}`}>
                                📱 {p.phone}
                              </a>
                            )}
                            <span>🐾 {p.services_count || 0} servicio(s)</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Link to={`/provider/${p.provider_id}`}><Button variant="outline" size="sm"><Eye className="w-4 h-4" /></Button></Link>
                        <Button size="sm" variant="outline" onClick={() => setLocationProvider(p)} className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid={`edit-location-${p.provider_id}`}>
                          <MapPin className="w-4 h-4 mr-1" />Ubicación
                        </Button>
                        {p.verified ? (
                          <Button size="sm" variant="outline" onClick={() => unverifyProvider(p.provider_id)}>Quitar Badge</Button>
                        ) : (
                          <Button size="sm" onClick={() => verifyProvider(p.provider_id)} className="bg-[#E6202E] hover:bg-[#D31522]"><Badge className="w-4 h-4 mr-1" />Verificar</Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                </div>
              )}
  
              {activeTab === 'clients' && (
                <div className="space-y-4" data-testid="clients-tab">
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <Input
                      placeholder="Buscar por nombre o email..."
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      className="max-w-sm"
                      data-testid="client-search"
                    />
                    <span className="text-sm text-gray-500">
                      {clients.filter(c => {
                        if (!clientSearch.trim()) return true;
                        const q = clientSearch.toLowerCase();
                        return (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q);
                      }).length} resultado(s)
                    </span>
                  </div>
  
                  {clients.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No hay clientes registrados</p>
                  ) : (
                    clients
                      .filter(c => {
                        if (!clientSearch.trim()) return true;
                        const q = clientSearch.toLowerCase();
                        return (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q);
                      })
                      .map(c => (
                        <div key={c.user_id} className={`p-4 rounded-xl border ${c.blocked ? 'bg-red-50/50 border-red-200' : 'bg-white border-gray-200'}`} data-testid={`client-${c.user_id}`}>
                          <div className="flex justify-between items-start gap-3 flex-wrap">
                            <div className="flex items-center gap-3 flex-1 min-w-[240px]">
                              <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden shrink-0 flex items-center justify-center">
                                {c.picture ? (
                                  <img src={c.picture.startsWith('http') ? c.picture : `${process.env.REACT_APP_BACKEND_URL}${c.picture}`} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <User className="w-6 h-6 text-gray-400" />
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="font-bold">{c.name || 'Sin nombre'}</h3>
                                  {c.has_subscription && (
                                    <span className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                                      Premium
                                    </span>
                                  )}
                                  {c.blocked && (
                                    <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full">
                                      Bloqueado
                                    </span>
                                  )}
                                  {c.auth_provider === 'google' && (
                                    <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">Google</span>
                                  )}
                                </div>
                                <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-600">
                                  {c.email && (
                                    <a href={`mailto:${c.email}`} className="flex items-center gap-1 hover:text-[#E6202E]">
                                      ✉️ {c.email}
                                    </a>
                                  )}
                                  {c.phone && (
                                    <a href={`https://wa.me/${String(c.phone).replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-green-600">
                                      📱 {c.phone}
                                    </a>
                                  )}
                                </div>
                                <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-500">
                                  <span>🐾 {c.pet_count || 0} mascota(s)</span>
                                  <span>📋 {c.request_count || 0} solicitud(es)</span>
                                  <span>📅 {c.booking_count || 0} reserva(s)</span>
                                  {c.created_at && (
                                    <span>Registrado: {new Date(c.created_at).toLocaleDateString('es-CL')}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={async () => {
                                  try {
                                    const res = await api.post(`/admin/clients/${c.user_id}/block`);
                                    toast.success(res.data.message);
                                    loadData();
                                  } catch (e) {
                                    toast.error('Error al actualizar estado');
                                  }
                                }}
                                className={c.blocked ? 'border-green-300 text-green-700 hover:bg-green-50' : 'border-orange-300 text-orange-700 hover:bg-orange-50'}
                                data-testid={`block-client-${c.user_id}`}
                              >
                                {c.blocked ? 'Desbloquear' : 'Bloquear'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={async () => {
                                  if (!window.confirm(`¿Eliminar definitivamente a ${c.name || c.email}? Esta acción borrará sus mascotas, solicitudes, suscripciones y favoritos. No se puede deshacer.`)) return;
                                  try {
                                    await api.delete(`/admin/clients/${c.user_id}`);
                                    toast.success('Cliente eliminado');
                                    loadData();
                                  } catch (e) {
                                    toast.error(e.response?.data?.detail || 'Error al eliminar');
                                  }
                                }}
                                className="text-red-600 border-red-300 hover:bg-red-50"
                                data-testid={`delete-client-${c.user_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              )}
  
  
              {activeTab === 'plans' && (
              <div className="space-y-4">
                <div className="flex justify-end">
                  <Button onClick={() => { setEditPlan(null); setShowPlanModal(true); }} className="bg-[#E6202E] hover:bg-[#D31522] text-white" data-testid="create-plan-button">
                    <Plus className="w-4 h-4 mr-1" />Nuevo Plan
                  </Button>
                </div>
                {plans.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No hay planes creados</p>
                ) : (
                  plans.map(p => (
                    <div key={p.plan_id} className={`p-5 rounded-xl border-2 ${p.active ? 'bg-white border-gray-200' : 'bg-gray-100 border-gray-300 opacity-60'} ${p.popular ? 'ring-2 ring-[#E6202E]' : ''}`} data-testid={`plan-card-${p.plan_id}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold text-lg">{p.name}</h3>
                            {p.popular && <span className="bg-[#E6202E] text-white text-xs px-2 py-0.5 rounded-full">Popular</span>}
                            {!p.active && <span className="bg-gray-500 text-white text-xs px-2 py-0.5 rounded-full">Inactivo</span>}
                          </div>
                          <p className="text-2xl font-bold text-[#E6202E] mt-1">${p.price_clp?.toLocaleString('es-CL')} CLP</p>
                          <p className="text-sm text-gray-500">{p.duration_months} {p.duration_months === 1 ? 'mes' : 'meses'}</p>
                          {p.features?.length > 0 && (
                            <ul className="mt-2 space-y-1">
                              {p.features.map((f, i) => <li key={i} className="text-sm text-gray-600 flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" />{f}</li>)}
                            </ul>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => togglePlan(p.plan_id)} data-testid={`toggle-plan-${p.plan_id}`}>
                            {p.active ? <ToggleRight className="w-4 h-4 text-green-600" /> : <ToggleLeft className="w-4 h-4 text-gray-400" />}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setEditPlan(p); setShowPlanModal(true); }} data-testid={`edit-plan-${p.plan_id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => deletePlan(p.plan_id)} className="text-red-600 border-red-300 hover:bg-red-50" data-testid={`delete-plan-${p.plan_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'metrics' && (
              <div className="space-y-6" data-testid="metrics-tab">
                <h3 className="text-lg font-bold text-gray-900">Actividad Ultimos 6 Meses</h3>
                <div className="bg-gray-50 rounded-xl p-6">
                  <MetricsChart data={metrics} />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-5">
                    <h4 className="font-semibold text-gray-700 mb-3">Resumen</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-gray-500">Clientes reales</span><span className="font-bold">{stats?.total_clients || 0}</span></div>     
                     <div className="flex justify-between"><span className="text-gray-500">Cuidadores reales</span><span className="font-bold">{stats?.total_providers || 0}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">Suscripciones activas</span><span className="font-bold text-[#E6202E]">{stats?.active_subscriptions || 0}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">Reseñas totales</span><span className="font-bold">{stats?.total_reviews || 0}</span></div>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-5">
                    <h4 className="font-semibold text-gray-700 mb-3">Cuidadores</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-gray-500">Aprobados</span><span className="font-bold text-green-600">{stats?.total_providers || 0}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">Pendientes</span><span className="font-bold text-orange-500">{stats?.pending_providers || 0}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">Verificados</span><span className="font-bold text-[#E6202E]">{stats?.verified_providers || 0}</span></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'sos' && (
              <div className="space-y-6" data-testid="sos-tab">
                <h3 className="text-lg font-bold text-gray-900">Configuracion SOS Veterinario</h3>
                <p className="text-sm text-gray-500">Configura el número de emergencia al que los cuidadores pueden llamar si tienen un problema con una mascota.</p>

                <div className="bg-gray-50 rounded-xl p-6 space-y-4">
                  <div className="flex items-center gap-3 mb-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={sosConfig.active || false} onChange={e => setSosConfig(prev => ({ ...prev, active: e.target.checked }))} className="w-5 h-5 accent-[#E6202E] rounded" data-testid="sos-active-toggle" />
                      <span className="font-semibold">{sosConfig.active ? 'SOS Activo' : 'SOS Desactivado'}</span>
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Nombre del Veterinario</label>
                    <input type="text" placeholder="Ej: Dr. Martinez" value={sosConfig.vet_name || ''} onChange={e => setSosConfig(prev => ({ ...prev, vet_name: e.target.value }))} className="w-full border rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6202E]" data-testid="sos-vet-name" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Numero de Telefono</label>
                    <input type="tel" placeholder="Ej: +56912345678" value={sosConfig.phone || ''} onChange={e => setSosConfig(prev => ({ ...prev, phone: e.target.value }))} className="w-full border rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6202E]" data-testid="sos-phone" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Horario de Atencion (texto descriptivo)</label>
                    <input type="text" placeholder="Ej: Lunes a Viernes 9:00 - 18:00" value={sosConfig.schedule || ''} onChange={e => setSosConfig(prev => ({ ...prev, schedule: e.target.value }))} className="w-full border rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6202E]" data-testid="sos-schedule" />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Hora inicio (0-23)</label>
                      <input type="number" min="0" max="23" value={sosConfig.start_hour ?? 8} onChange={e => setSosConfig(prev => ({ ...prev, start_hour: parseInt(e.target.value) || 0 }))} className="w-full border rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6202E]" data-testid="sos-start-hour" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Hora fin (0-23)</label>
                      <input type="number" min="0" max="23" value={sosConfig.end_hour ?? 20} onChange={e => setSosConfig(prev => ({ ...prev, end_hour: parseInt(e.target.value) || 0 }))} className="w-full border rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6202E]" data-testid="sos-end-hour" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">El boton SOS solo estara activo entre la hora inicio y hora fin (hora de Chile). Fuera de horario, los cuidadores suscritos veran el boton deshabilitado.</p>

                  <Button
                    onClick={async () => {
                      setSavingSos(true);
                      try {
                        await api.put('/admin/sos', sosConfig);
                        toast.success('Configuracion SOS guardada');
                      } catch { toast.error('Error al guardar'); }
                      finally { setSavingSos(false); }
                    }}
                    disabled={savingSos}
                    className="bg-[#E6202E] hover:bg-[#D31522]"
                    data-testid="save-sos-btn"
                  >
                    {savingSos ? 'Guardando...' : 'Guardar Configuracion SOS'}
                  </Button>
                </div>
              </div>
            )}

            {activeTab === 'promos' && (
              <AdminPromoCodes />
            )}
          </div>
        </div>
      </div>

      {showPlanModal && (
        <PlanModal
          plan={editPlan}
          onClose={() => setShowPlanModal(false)}
          onSave={() => { setShowPlanModal(false); loadData(); }}
        />
      )}

      {/* Verification Documents Modal */}
      {viewingDocs && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="verification-docs-modal">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center bg-gradient-to-r from-[#E6202E] to-[#D31522] text-white">
              <div>
                <h3 className="text-xl font-bold">Documentos de Verificación</h3>
                <p className="text-sm opacity-80">{viewingDocs.business_name}</p>
              </div>
              <button onClick={() => setViewingDocs(null)} className="p-2 hover:bg-white/20 rounded-full">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="grid md:grid-cols-2 gap-6">
                {/* ID Front */}
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Camera className="w-4 h-4 text-[#E6202E]" />
                    Carnet (Frente)
                  </h4>
                  {viewingDocs.id_front_photo ? (
                    <a href={`${API_BASE}${viewingDocs.id_front_photo}`} target="_blank" rel="noreferrer">
                      <img 
                        src={`${API_BASE}${viewingDocs.id_front_photo}`} 
                        alt="Carnet frente" 
                        className="w-full h-48 object-cover rounded-xl border-2 border-gray-200 hover:border-[#E6202E] transition-colors cursor-pointer"
                      />
                    </a>
                  ) : (
                    <div className="w-full h-48 bg-gray-100 rounded-xl flex items-center justify-center text-gray-400">
                      No proporcionado
                    </div>
                  )}
                </div>

                {/* ID Back */}
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Camera className="w-4 h-4 text-[#E6202E]" />
                    Carnet (Dorso)
                  </h4>
                  {viewingDocs.id_back_photo ? (
                    <a href={`${API_BASE}${viewingDocs.id_back_photo}`} target="_blank" rel="noreferrer">
                      <img 
                        src={`${API_BASE}${viewingDocs.id_back_photo}`} 
                        alt="Carnet dorso" 
                        className="w-full h-48 object-cover rounded-xl border-2 border-gray-200 hover:border-[#E6202E] transition-colors cursor-pointer"
                      />
                    </a>
                  ) : (
                    <div className="w-full h-48 bg-gray-100 rounded-xl flex items-center justify-center text-gray-400">
                      No proporcionado
                    </div>
                  )}
                </div>

                {/* Selfie */}
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <User className="w-4 h-4 text-[#E6202E]" />
                    Selfie
                  </h4>
                  {viewingDocs.selfie_photo ? (
                    <a href={`${API_BASE}${viewingDocs.selfie_photo}`} target="_blank" rel="noreferrer">
                      <img 
                        src={`${API_BASE}${viewingDocs.selfie_photo}`} 
                        alt="Selfie" 
                        className="w-full h-48 object-cover rounded-xl border-2 border-gray-200 hover:border-[#E6202E] transition-colors cursor-pointer"
                      />
                    </a>
                  ) : (
                    <div className="w-full h-48 bg-gray-100 rounded-xl flex items-center justify-center text-gray-400">
                      No proporcionado
                    </div>
                  )}
                </div>

                {/* Background Certificate */}
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#E6202E]" />
                    Certificado de Antecedentes
                  </h4>
                  {viewingDocs.background_certificate ? (
                    viewingDocs.background_certificate.endsWith('.pdf') ? (
                      <a 
                        href={`${API_BASE}${viewingDocs.background_certificate}`} 
                        target="_blank" 
                        rel="noreferrer"
                        className="w-full h-48 bg-blue-50 rounded-xl flex flex-col items-center justify-center text-blue-600 hover:bg-blue-100 transition-colors"
                      >
                        <FileText className="w-12 h-12 mb-2" />
                        <span className="font-medium">Ver PDF</span>
                      </a>
                    ) : (
                      <a href={`${API_BASE}${viewingDocs.background_certificate}`} target="_blank" rel="noreferrer">
                        <img 
                          src={`${API_BASE}${viewingDocs.background_certificate}`} 
                          alt="Certificado" 
                          className="w-full h-48 object-cover rounded-xl border-2 border-gray-200 hover:border-[#E6202E] transition-colors cursor-pointer"
                        />
                      </a>
                    )
                  ) : (
                    <div className="w-full h-48 bg-gray-100 rounded-xl flex items-center justify-center text-gray-400">
                      No proporcionado
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 p-4 bg-yellow-50 rounded-xl">
                <p className="text-sm text-yellow-800">
                  <strong>Instrucciones:</strong> Compara la foto del carnet con la selfie para verificar que sea la misma persona. 
                  Si todo esta correcto, aprueba y verifica al cuidador.
                </p>
              </div>
            </div>

            <div className="p-4 border-t bg-gray-50 flex gap-3">
              <Button 
                onClick={() => { rejectProvider(viewingDocs.provider_id); setViewingDocs(null); }}
                variant="outline" 
                className="flex-1 text-red-600 border-red-300"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Rechazar
              </Button>
              <Button 
                onClick={async () => { 
                  await approveProvider(viewingDocs.provider_id); 
                  await verifyProvider(viewingDocs.provider_id);
                  setViewingDocs(null); 
                }}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Aprobar y Verificar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Location editor modal */}
      {locationProvider && (
        <LocationEditorModal
          provider={locationProvider}
          onClose={() => setLocationProvider(null)}
          onSaved={() => {
            setLocationProvider(null);
            loadData();
          }}
        />
      )}
    </div>
  );
}
